import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";

const DEMO_MSG = "Create your free account to do this";

export function useCreateAccount() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  return async () => {
    await logout();
    navigate("/join");
  };
}

export function showDemoPrompt(createAccount) {
  toast("This is a sample profile", {
    description: "Create your free account to save readings, chat and personalise Astra.",
    action: { label: "Create free account", onClick: createAccount },
  });
}

// Shows a "Create your free account" prompt whenever the demo account hits a blocked action.
export function DemoGuard() {
  const createAccount = useCreateAccount();
  useEffect(() => {
    const id = api.interceptors.response.use(undefined, (err) => {
      if (err.response?.status === 403 && err.response?.data?.detail === DEMO_MSG) {
        showDemoPrompt(createAccount);
      }
      return Promise.reject(err);
    });
    return () => api.interceptors.response.eject(id);
  }, [createAccount]);
  return null;
}

export function DemoBanner() {
  const { user } = useAuth();
  const createAccount = useCreateAccount();
  if (!user?.is_demo) return null;
  return (
    <div data-testid="demo-banner" className="mx-5 mt-3 flex items-center gap-3 bg-[#0F1523] border border-[#D4AF37]/35 rounded-2xl px-4 py-2.5">
      <Sparkles size={15} className="gold-text shrink-0" />
      <p className="text-sm text-[#D6D0C5] flex-1">You're exploring a sample profile.</p>
      <button data-testid="btn-demo-create-account" onClick={createAccount} className="text-sm gold-text whitespace-nowrap hover:underline">
        Create your free account
      </button>
    </div>
  );
}
